import React, { useState, useEffect } from 'react';
import { Upload, ChevronRight, CheckCircle, AlertCircle, Download, Sparkles } from 'lucide-react';
import { piApi } from '../../services/piApi';
import { hasPI } from '../../lib/permissions';
import { Navigate, useNavigate } from 'react-router-dom';
import '../pi/PIShell.css';

const IMPORT_TYPES = [
  { id: 'portcos',     label: 'Portfolio Companies',        desc: 'Import company records with financial metadata' },
  { id: 'initiatives', label: 'VCP Initiatives',            desc: 'Import value creation plan items' },
  { id: 'bridge',      label: 'EBITDA / Financial Data',    desc: 'Import historical bridge and financial data' },
  { id: 'talent',      label: 'Leadership Profiles',        desc: 'Import talent and management team records' },
];

// Per-type column options for the mapping step
const COLUMN_OPTIONS = {
  portcos:     ['name', 'sector', 'close_date', 'entry_ebitda', 'entry_revenue', 'entry_multiple', 'acquisition_price', 'target_irr', 'target_moic'],
  initiatives: ['portco_id', 'title', 'type', 'status', 'owner', 'due_date', 'value_impact', 'notes'],
  bridge:      ['portco_id', 'label', 'category', 'underwritten_value', 'actual_value', 'sort_order'],
  talent:      ['portco_id', 'full_name', 'role_title', 'level', 'status', 'tenure_months', 'has_exit_experience', 'notes'],
};

const STEPS = ['Choose Type', 'Upload', 'Map Columns', 'AI Normalize', 'Review', 'Done'];

export default function PIImportWizard({ currentUser }) {
  const navigate = useNavigate();
  const [step, setStep]           = useState(0);
  const [importType, setImportType] = useState('portcos');
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [reviewData, setReviewData] = useState([]);
  const [result, setResult]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // Auto-trigger AI normalize when PDF lands on step 3
  // Declared before any conditional return to satisfy Rules of Hooks.
  // Logic is inlined here rather than calling handleAINormalize to avoid a
  // forward-reference (handleAINormalize is defined later in the function body).
  useEffect(() => {
    if (step !== 3 || preview.length === 0 || loading || reviewData.length > 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    piApi.previewImport(importType, {}, preview)
      .then(res => { if (!cancelled) { setReviewData(res.data.records || []); setStep(4); } })
      .catch(() => { if (!cancelled) setError('AI normalization failed. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (!hasPI(currentUser, 'pi.import')) {
    return <Navigate to="/pi/dashboard" replace />;
  }

  // ── Step 2 → 3: column map confirmed, run AI normalize ──────────────────────
  const handleColumnMapConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await piApi.previewImport(importType, columnMap, preview);
      setReviewData(res.data.records || []);
      setStep(4);
    } catch {
      setError('AI normalization failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 1: parse uploaded file ───────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const res = await piApi.parseImportFile(importType, file);
      const isPDF = file.name.toLowerCase().endsWith('.pdf');
      setPreview(res.data.preview || []);
      setColumnMap(res.data.suggestedMapping || {});
      if (isPDF) {
        // PDF: skip column mapping, go straight to AI normalize (step 3)
        setStep(3);
      } else {
        setStep(2);
      }
    } catch {
      setError('Failed to parse file. Check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3 (PDF path): AI-normalize without manual column mapping ─────────────
  const handleAINormalize = async () => {
    setLoading(true);
    setError(null);
    try {
      // For PDFs, preview is raw text rows with no column headers — pass empty columnMap
      const res = await piApi.previewImport(importType, {}, preview);
      setReviewData(res.data.records || []);
      setStep(4);
    } catch {
      setError('AI normalization failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4: confirm import ─────────────────────────────────────────────────────
  const handleImport = async () => {
    setLoading(true);
    try {
      const clean = reviewData.filter(r => !r._error);
      const res = await piApi.confirmImport(importType, clean);
      setResult({ ...res.data, errors: reviewData.length - clean.length });
      setStep(5);
    } catch {
      setError('Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const typeConfig = IMPORT_TYPES.find(t => t.id === importType) || IMPORT_TYPES[0];
  const colOptions = COLUMN_OPTIONS[importType] || [];

  // ── Step indicator ─────────────────────────────────────────────────────────────
  const StepBar = () => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 28, flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, opacity: i > step ? 0.35 : 1 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: i < step ? '#4f46e5' : i === step ? '#eef2ff' : '#f1f5f9',
              color: i < step ? 'white' : i === step ? '#4f46e5' : '#94a3b8',
              border: i === step ? '2px solid #4f46e5' : '2px solid transparent',
            }}>{i < step ? '✓' : i + 1}</div>
            <span style={{ fontSize: 11.5, fontWeight: i === step ? 600 : 400, color: i === step ? '#1e293b' : '#94a3b8' }}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <ChevronRight size={11} style={{ color: '#cbd5e1', flexShrink: 0 }} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div style={{ padding: '32px 40px', maxWidth: 780, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>PI Import Wizard</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 28 }}>Import portfolio data from CSV, Excel, or PDF documents.</p>

      <StepBar />

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
          <AlertCircle size={15} /> {error}
          <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18 }} onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* ── Step 0: Choose type ──────────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="pi-card">
          <div className="pi-card-header"><span className="pi-card-title">What would you like to import?</span></div>
          <div className="pi-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {IMPORT_TYPES.map(t => (
              <label key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                border: `2px solid ${importType === t.id ? '#4f46e5' : '#e2e8f0'}`,
                borderRadius: 8, cursor: 'pointer',
                background: importType === t.id ? '#eef2ff' : 'white',
              }}>
                <input type="radio" name="importType" value={t.id} checked={importType === t.id}
                  onChange={() => setImportType(t.id)} style={{ accentColor: '#4f46e5' }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: importType === t.id ? 600 : 500, color: '#334155' }}>{t.label}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button className="pi-btn-ghost" style={{ fontSize: 12 }}
              onClick={() => piApi.downloadTemplate(importType)}>
              <Download size={12} /> Download {typeConfig.label} Template
            </button>
            <button className="pi-btn-primary" onClick={() => setStep(1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 1: Upload ───────────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="pi-card">
          <div className="pi-card-header"><span className="pi-card-title">Upload your file — {typeConfig.label}</span></div>
          <div className="pi-card-body">
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              padding: 32, border: '2px dashed #c7d2fe', borderRadius: 10, cursor: 'pointer',
              background: file ? '#f0fdf4' : '#fafbff',
            }}>
              <Upload size={28} style={{ color: file ? '#16a34a' : '#4f46e5', opacity: 0.8 }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
                  {file ? file.name : 'Drag & drop or click to select'}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>CSV, Excel (.xlsx), or PDF</div>
              </div>
              <input type="file" accept=".csv,.xlsx,.xls,.pdf" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
            </label>
          </div>
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
            <button className="pi-btn-ghost" onClick={() => setStep(0)}>Back</button>
            <button className="pi-btn-primary" onClick={handleUpload} disabled={!file || loading}>
              {loading ? 'Parsing...' : 'Upload & Parse'} <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Column mapping ───────────────────────────────────────────────── */}
      {step === 2 && (
        <div className="pi-card">
          <div className="pi-card-header"><span className="pi-card-title">Map columns to DealNexa fields</span></div>
          <div className="pi-card-body">
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              Review auto-detected mappings. Set any mismatches before continuing.
            </p>
            {Object.keys(columnMap).length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>No columns detected in file.</div>
            )}
            {Object.entries(columnMap).map(([source, target]) => (
              <div key={source} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 200, fontSize: 13, color: '#475569', fontWeight: 500,
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 5, padding: '5px 9px' }}>
                  {source}
                </div>
                <ChevronRight size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                <select value={target || ''} onChange={e => setColumnMap(m => ({ ...m, [source]: e.target.value }))}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#334155' }}>
                  <option value="">— skip —</option>
                  {colOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
            <button className="pi-btn-ghost" onClick={() => setStep(1)}>Back</button>
            <button className="pi-btn-primary" onClick={handleColumnMapConfirm} disabled={loading}>
              {loading ? 'Normalizing...' : 'Continue with AI'} <Sparkles size={13} />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: AI Normalization (PDF path, auto-triggered) ───────────────────── */}
      {step === 3 && (
        <div className="pi-card">
          <div className="pi-card-header"><span className="pi-card-title">AI Document Analysis</span></div>
          <div className="pi-card-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
            {loading ? (
              <>
                <div className="pi-spinner" style={{ margin: '0 auto 16px' }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 8 }}>
                  AI is extracting records from your document...
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  Claude is reading the PDF and normalizing data to the {typeConfig.label} schema.
                </div>
              </>
            ) : (
              <>
                <AlertCircle size={32} style={{ color: '#d97706', marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 16 }}>
                  AI normalization did not start automatically.
                </div>
                <button className="pi-ai-btn" onClick={handleAINormalize}>
                  <Sparkles size={13} /> Run AI Normalize
                </button>
              </>
            )}
          </div>
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-start' }}>
            <button className="pi-btn-ghost" onClick={() => setStep(1)} disabled={loading}>Back</button>
          </div>
        </div>
      )}

      {/* ── Step 4: Review ───────────────────────────────────────────────────────── */}
      {step === 4 && (
        <div className="pi-card">
          <div className="pi-card-header">
            <span className="pi-card-title">Review {reviewData.length} extracted records</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <span className="pi-badge pi-badge-green">{reviewData.filter(r => !r._error).length} ready</span>
              {reviewData.filter(r => r._error).length > 0 && (
                <span className="pi-badge pi-badge-red">{reviewData.filter(r => r._error).length} errors</span>
              )}
            </div>
          </div>
          {reviewData.length === 0 ? (
            <div className="pi-empty" style={{ padding: 32 }}>
              <div className="pi-empty-title">No records extracted</div>
              <div className="pi-empty-sub">The AI could not find valid records in the file. Try a different file or import type.</div>
            </div>
          ) : (
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#fafbff', position: 'sticky', top: 0 }}>
                    {['Name / Title', 'Sector / Type', 'Date', 'Value', 'Status'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reviewData.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc', background: r._error ? '#fff5f5' : 'white' }}>
                      <td style={{ padding: '9px 14px', color: '#334155', fontWeight: 500 }}>
                        {r.name || r.title || r.full_name || r.label || '—'}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#64748b' }}>
                        {r.sector || r.type || r.category || r.level || '—'}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#64748b' }}>
                        {r.close_date || r.due_date || r.target_date || '—'}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#64748b' }}>
                        {r.entry_ebitda != null ? `$${r.entry_ebitda}M EBITDA`
                          : r.underwritten_value != null ? `$${r.underwritten_value}M`
                          : r.modeled_value != null ? `$${r.modeled_value}M`
                          : '—'}
                      </td>
                      <td style={{ padding: '9px 14px' }}>
                        {r._error
                          ? <span className="pi-badge pi-badge-red" title={r._error}>Error</span>
                          : <span className="pi-badge pi-badge-green">Ready</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
            <button className="pi-btn-ghost" onClick={() => setStep(file?.name?.endsWith('.pdf') ? 3 : 2)}>Back</button>
            <button className="pi-btn-primary" onClick={handleImport}
              disabled={loading || reviewData.filter(r => !r._error).length === 0}>
              {loading ? 'Importing...' : `Import ${reviewData.filter(r => !r._error).length} Records`}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 5: Done ─────────────────────────────────────────────────────────── */}
      {step === 5 && result && (
        <div className="pi-card">
          <div className="pi-card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <CheckCircle size={44} style={{ color: '#16a34a', marginBottom: 14 }} />
            <div style={{ fontSize: 19, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
              {result.imported} {typeConfig.label.toLowerCase()} imported
            </div>
            {result.errors > 0 && (
              <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 20 }}>
                {result.errors} records skipped due to errors.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
              <button className="pi-btn-ghost" onClick={() => { setStep(0); setFile(null); setReviewData([]); setResult(null); }}>
                Import More
              </button>
              <button className="pi-btn-primary" onClick={() => navigate('/pi/dashboard')}>
                View Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
